<template>
  <transition mode="out-in" name="wrapUpTimer">
    <v-progress-circular
      v-if="isOnWrapUp && !interaction.isPreview"
      :rotate="-90"
      :size="size - 2"
      :width="2"
      :value="progressCircularValue"
      :color="'#fff'"
      :style="{ fontSize: progressFontSize }"
    >
      {{ fixedWrapUpTime }}
    </v-progress-circular>
    <v-avatar v-else-if="interaction.isConference" :size="size" color="var(--v-accent-base)">
      <v-icon :style="{ fontSize: avatarFontSize }">mdi-account-group</v-icon>
    </v-avatar>
    <EcsAvatar
      v-else
      :agent-id="isAgent ? interaction.receptor.id : null"
      :contact-id="isAgent ? null : interaction.receptor.id"
      :alt="interaction.receptor ? interaction.receptor.name : ''"
      :size="size"
      :stateful="isAgent"
    />
  </transition>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import { CONTACT_TYPE } from '@/constants/contactConstants';

export default {
  name: 'EcsInteractionAvatar',

  components: { EcsAvatar },

  props: {
    size: {
      type: Number,
      default: 40
    },
    interaction: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      remainingWrapUpTime: 0
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('ELEMENTS', ['getElementsForInteraction']),

    isAgent() {
      return [CONTACT_TYPE.agent, CONTACT_TYPE.supervisor, CONTACT_TYPE.enterprise].includes(
        this.interaction.receptor.contactType
      );
    },

    isOnWrapUp() {
      return this.interaction.isOnWrapUp && this.remainingWrapUpTime > 0;
    },

    iconSize() {
      return Math.max(this.size / 1.8, 10);
    },

    progressCircularValue() {
      return (
        (100 / this.interaction.timing.wrapUpTime) * (this.interaction.timing.wrapUpTime - this.remainingWrapUpTime)
      );
    },

    progressFontSize() {
      return `${this.size / 2.7}px`;
    },

    avatarFontSize() {
      return `${this.iconSize}px`;
    },

    fixedWrapUpTime() {
      return (this.remainingWrapUpTime + 1).toFixed(0);
    }
  },

  watch: {
    interaction: {
      handler(updatedInteraction) {
        if (updatedInteraction.isOnWrapUp) {
          this.startWrapUpTimer();
        }
      },
      deep: true
    }
  },

  mounted() {
    this.startWrapUpTimer();
  },

  methods: {
    startWrapUpTimer() {
      this.remainingWrapUpTime = this.getRemainingWrapUpTime();
      const millisecondsPerProgressStep = (this.interaction.timing.wrapUpTime * 1000) / 100;

      const timer = setInterval(() => {
        if (this.remainingWrapUpTime > 0) {
          this.remainingWrapUpTime = this.getRemainingWrapUpTime();
        } else {
          clearInterval(timer);
        }
      }, millisecondsPerProgressStep);
    },

    getRemainingWrapUpTime() {
      const {
        timestamp,
        timing: { wrapUpTime, wrapUpTimeout, lastStateStartTime }
      } = this.interaction;

      const wrapUpEndTime = moment(lastStateStartTime || timestamp).add(wrapUpTimeout || wrapUpTime, 'seconds');
      return Math.max(wrapUpEndTime.diff(moment(), 'seconds', true), 0);
    }
  }
};
</script>

<style scoped>
.v-progress-circular {
  background-color: var(--v-secondary-base);
  -moz-border-radius: 30px;
  -webkit-border-radius: 30px;
  border-radius: 30px;
}
.ecs-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--size);
  height: var(--size);
  border: var(--border-width);
  border-style: solid;
  border-radius: 50%;
  border-color: var(--border-color);
  font-size: var(--font-size);
}

.avatar-enter-active {
  animation: scale-in 250ms;
}

.avatar-leave-active {
  animation: scale-in 250ms reverse;
}

/* CSS Animation Keyframes */
@keyframes scale-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
